/**
 * MachineScheduleHeader.tsx
 * Sticky top header row for the Machine Schedule grid: the "Machine / Type"
 * corner cell + one column per day (month label on the 1st, weekday, day-of-month).
 * Highlights today and the hovered column. Extracted from MachineScheduleGrid to
 * keep that file under the 600-line cap.
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  LEFT_COL_WIDTH,
  DAY_COL_MIN,
  dayAbbrev,
  dayNum,
  monthAbbrev,
  isWeekend,
  isToday,
} from './scheduleGridShared';

interface MachineScheduleHeaderProps {
  days: string[];
  totalDays: number;
  /** Index of the column currently hovered (for cross-highlight), or null. */
  hoverCol: number | null;
  /** Width of each day column in px (zoom / density). */
  dayColWidth?: number;
}

export const MachineScheduleHeader: React.FC<MachineScheduleHeaderProps> = ({
  days,
  totalDays,
  hoverCol,
  dayColWidth = DAY_COL_MIN,
}) => {
  const theme = useTheme();
  const headerBg = theme.table.headerBg;
  const borderColor = theme.border.color;
  const weekendBg = alpha(theme.palette.text.primary, 0.03);
  const colHighlight = alpha(theme.palette.primary.main, 0.07);
  // Today header cell: blue accent tint + left edge, distinct from grey weekends (BUG-2026-0096).
  const todayBg = alpha(theme.schedule.todayColumn, 0.22);
  const todayBorder = `2px solid ${alpha(theme.schedule.todayColumn, 0.55)}`;

  return (
    <Box
      sx={{
        display: 'flex',
        position: 'sticky',
        top: 0,
        zIndex: 5,
        backgroundColor: headerBg,
        borderBottom: `1px solid ${borderColor}`,
        minWidth: LEFT_COL_WIDTH + totalDays * dayColWidth,
      }}
    >
      <Box
        sx={{
          width: LEFT_COL_WIDTH,
          minWidth: LEFT_COL_WIDTH,
          flexShrink: 0,
          px: 1.5,
          py: 1,
          borderRight: `1px solid ${borderColor}`,
          display: 'flex',
          alignItems: 'center',
          // Freeze the corner cell with the machine column — without this the
          // "Machine / Type" label slid away on horizontal scroll while the
          // row labels below stayed pinned (sticky-headers UX pass follow-up).
          position: 'sticky',
          left: 0,
          zIndex: 1,
          backgroundColor: headerBg,
        }}
      >
        <Typography variant="caption" fontWeight={700} color="text.secondary">
          Machine / Type
        </Typography>
      </Box>

      {days.map((day, i) => {
        const isFirst = i === 0 || day.slice(8) === '01';
        return (
          <Box
            key={day}
            sx={{
              // Fixed width to match the body's day cells (Excel-like zoom).
              width: dayColWidth,
              minWidth: dayColWidth,
              flexShrink: 0,
              borderRight: `1px solid ${alpha(borderColor, 0.5)}`,
              borderLeft: isToday(day)
                ? todayBorder
                : day.slice(8) === '01' && i !== 0
                  ? `2px solid ${borderColor}`
                  : undefined,
              backgroundColor:
                i === hoverCol
                  ? colHighlight
                  : isToday(day)
                    ? todayBg
                    : isWeekend(day)
                      ? weekendBg
                      : 'inherit',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 0.25,
            }}
          >
            {isFirst && (
              <Typography
                variant="caption"
                sx={{ fontSize: '0.6rem', fontWeight: 700, color: theme.palette.text.secondary, lineHeight: 1 }}
              >
                {monthAbbrev(day)}
              </Typography>
            )}
            {!isFirst && <Box sx={{ height: '0.6rem' }} />}
            <Typography
              variant="caption"
              sx={{
                fontWeight: isToday(day) ? 800 : 500,
                color: isToday(day) ? theme.palette.primary.main : 'text.secondary',
                fontSize: '0.65rem',
                lineHeight: 1.2,
              }}
            >
              {dayAbbrev(day)}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontWeight: isToday(day) ? 800 : 400,
                color: isToday(day) ? theme.palette.primary.main : 'text.primary',
                fontSize: '0.72rem',
              }}
            >
              {dayNum(day)}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};
